import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ChatMessage,
  QuickReplySuggestion,
  TripRequestResult,
} from "../interfaces/chat.interfaces";
import {
  startChatSession,
  sendChatMessage,
  getChatMessages,
  getChatSession,
} from "../controller/chat_controller";
import { wrapError } from "../utils/errorHandler";

const STORAGE_SESSION_KEY = "cartago_chat_session_id";
const STORAGE_LANGUAGE_KEY = "cartago_chat_language";
const STORAGE_PASSWORD_PREFIX = "cartago_trip_request_password_";

interface ChatContextType {
  isOpen: boolean;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  language: "en" | "es";
  setLanguage: (lang: "en" | "es") => void;
  messages: ChatMessage[];
  sessionId: string | null;
  isLoading: boolean;
  isComplete: boolean;
  currentStep: string | null;
  suggestions: QuickReplySuggestion[];
  tripRequest: TripRequestResult | null;
  startSession: (language?: "en" | "es") => Promise<string | null>;
  sendMessage: (content: string) => Promise<void>;
  restoreSession: (sessionId: string) => Promise<boolean>;
  clearSession: () => void;
  startNewChat: () => Promise<void>;
}

const ChatContext = createContext<ChatContextType | null>(null);

export function useChatContext(): ChatContextType {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
}

export function ChatProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const [language, setLanguageState] = useState<"en" | "es">(() => {
    const langParam = searchParams.get("lang");
    if (langParam === "en" || langParam === "es") return langParam;
    return (localStorage.getItem(STORAGE_LANGUAGE_KEY) as "en" | "es") || "es";
  });

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<QuickReplySuggestion[]>([]);
  const [tripRequest, setTripRequest] = useState<TripRequestResult | null>(null);
  const initializedRef = useRef(false);

  const setLanguage = useCallback((lang: "en" | "es") => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_LANGUAGE_KEY, lang);
  }, []);

  const openChat = useCallback(() => setIsOpen(true), []);
  const closeChat = useCallback(() => setIsOpen(false), []);
  const toggleChat = useCallback(() => setIsOpen((prev) => !prev), []);

  const startSession = useCallback(async (lang?: "en" | "es"): Promise<string | null> => {
    setIsLoading(true);
    try {
      const response = await startChatSession({ channel: "web", ...(lang && { language: lang }) });
      if (response.success && response.data) {
        const newSessionId = response.data.sessionId;
        setSessionId(newSessionId);
        localStorage.setItem(STORAGE_SESSION_KEY, newSessionId);

        const greetingMessage: ChatMessage = {
          messageId: `greeting-${Date.now()}`,
          role: "assistant",
          content: response.data.greeting,
          stepName: "greeting",
          createdAt: new Date().toISOString(),
        };
        setMessages([greetingMessage]);
        setCurrentStep("greeting");
        setIsComplete(false);
        setSuggestions([]);
        setTripRequest(null);
        return newSessionId;
      }
      return null;
    } catch (error) {
      wrapError(error, "Failed to start chat session");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const sendMessage = useCallback(
    async (content: string): Promise<void> => {
      if (!sessionId || !content.trim()) return;

      const userMessage: ChatMessage = {
        messageId: `user-${Date.now()}`,
        role: "user",
        content: content.trim(),
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setSuggestions([]);
      setIsLoading(true);

      try {
        const response = await sendChatMessage(sessionId, content.trim());
        if (response.success && response.data) {
          const assistantMessage: ChatMessage = {
            messageId: response.data.messageId,
            role: "assistant",
            content: response.data.response,
            extractedData: response.data.extractedData,
            createdAt: new Date().toISOString(),
            suggestions: response.data.suggestions,
          };
          setMessages((prev) => [...prev, assistantMessage]);
          setCurrentStep(response.data.currentStep);
          setSuggestions(response.data.suggestions || []);

          if (response.data.isComplete) {
            setIsComplete(true);
          }

          if (response.data.tripRequest) {
            const tr = response.data.tripRequest;
            setTripRequest(tr);

            if (tr.password && tr.requestId) {
              localStorage.setItem(`${STORAGE_PASSWORD_PREFIX}${tr.requestId}`, tr.password);
            }

            if (tr.hasCustomItinerary && tr.requestId) {
              navigate(`/my-trip/${tr.requestId}`);
            }
          }
        }
      } catch (error) {
        wrapError(error, "Failed to send message");
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId, navigate],
  );

  const restoreSession = useCallback(async (id: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const [messagesResponse, sessionResponse] = await Promise.all([
        getChatMessages(id),
        getChatSession(id),
      ]);

      if (messagesResponse.success && messagesResponse.data) {
        setSessionId(id);
        setMessages(messagesResponse.data.messages);

        const lastAssistantMessage = [...messagesResponse.data.messages]
          .reverse()
          .find((m) => m.role === "assistant");

        if (lastAssistantMessage?.suggestions) {
          setSuggestions(lastAssistantMessage.suggestions);
        }

        if (sessionResponse.success && sessionResponse.data) {
          setIsComplete(sessionResponse.data.isComplete);
          if (sessionResponse.data.tripRequestId) {
            const savedPassword = localStorage.getItem(
              `${STORAGE_PASSWORD_PREFIX}${sessionResponse.data.tripRequestId}`
            ) || "";
            setTripRequest({
              requestId: sessionResponse.data.tripRequestId,
              password: savedPassword,
            });
          }
        }

        return true;
      }
      return false;
    } catch (error) {
      wrapError(error, "Failed to restore chat session");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearSession = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setIsLoading(false);
    setIsComplete(false);
    setCurrentStep(null);
    setSuggestions([]);
    setTripRequest(null);
    localStorage.removeItem(STORAGE_SESSION_KEY);
  }, []);

  const startNewChat = useCallback(async () => {
    clearSession();
    await startSession(language);
  }, [clearSession, startSession, language]);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const savedSessionId = localStorage.getItem(STORAGE_SESSION_KEY);
    if (savedSessionId) {
      restoreSession(savedSessionId).then((success) => {
        if (!success) {
          localStorage.removeItem(STORAGE_SESSION_KEY);
        }
      });
    }
  }, [restoreSession]);

  return (
    <ChatContext.Provider
      value={{
        isOpen,
        openChat,
        closeChat,
        toggleChat,
        language,
        setLanguage,
        messages,
        sessionId,
        isLoading,
        isComplete,
        currentStep,
        suggestions,
        tripRequest,
        startSession,
        sendMessage,
        restoreSession,
        clearSession,
        startNewChat,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}
